import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import find from 'lodash/find';
import get from 'lodash/get';
import {
  selectCurrentWorkspace,
  selectAllHostsCountWs
} from 'store/Faraday/selectors';
import { selectActiveWorkspaces } from 'store/Workspace/selectors';
import { selectPentestStatus } from 'store/Dashboard/selectors';
import { VictoryPie, VictoryContainer } from 'victory';
import colors from 'Styles/colors';
import {
  Wrapper,
  TitleRow,
  WorkspaceName,
  ActiveBadge,
  MetaRow,
  MetaItem,
  MetaLabel,
  MetaValue,
  TopSection,
  InfoContainer,
  Title,
  Subtitle,
  Dates,
  MetricsContainer,
  MetricCard,
  MetricValue as MetricValueStyled,
  MetricLabel,
  MetricContext,
  TimelineSection,
  TimelineTitle,
  TimelinePhases,
  PhaseItem,
  PhaseChip,
  ChipIcon,
  ChipText,
  StatusLabel,
  TimelineConnector,
  TitleRight,
  SubtitleRight,
  DateLabel
} from './styled';
import { Divider } from '@material-ui/core';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  }).format(date);
};

const RISK_COLOR = {
  purple: '#9B59B6',
  red: '#dc4a4a',
  orange: '#f1ae62',
  green: '#88cc3f'
};

const getColorKey = (risk) => {
  if (risk >= 90) return 'purple';
  if (risk >= 70) return 'red';
  if (risk >= 40) return 'orange';
  return 'green';
};

const GaugeComponent = React.memo(({ value }) => {
  const gaugeData = useMemo(
    () => [
      { name: 'risk', value: value || 0 },
      { name: 'rest', value: 100 - (value || 0) }
    ],
    [value]
  );

  const colorKey = getColorKey(value || 0);
  const color = RISK_COLOR[colorKey];

  return (
    <div style={ { position: 'relative', width: 120, height: 47 } }>
      <VictoryPie
        startAngle={ -90 }
        endAngle={ 90 }
        data={ gaugeData }
        x="name"
        y="value"
        style={ {
          data: {
            fill: ({ datum }) => (datum.name === 'risk' ? color : colors.blue8),
            stroke: 'none'
          }
        } }
        height={ 94 }
        width={ 120 }
        radius={ 44 }
        innerRadius={ 33 }
        cornerRadius={ ({ datum }) => (datum.name === 'risk' ? 8 : 0) }
        labels={ () => '' }
        containerComponent={ <VictoryContainer responsive={ false } /> }
      />
    </div>
  );
});

GaugeComponent.displayName = 'GaugeComponent';

const STATUS_ICON = {
  'completed': '✓',
  'in-progress': '↻',
  'pending': '···'
};

const STATUS_LABEL = {
  'completed': 'COMPLETED',
  'in-progress': 'IN PROGRESS',
  'pending': 'PENDING'
};

const EngagementTimeline = ({ phases }) => {
  if (!phases || phases.length === 0) return null;

  return (
    <TimelinePhases>
      {phases.map((phase, index) => (
        <React.Fragment key={ phase.phase }>
          {index > 0 && (
            <TimelineConnector $fromStatus={ phases[index - 1].status } />
          )}
          <PhaseItem>
            <PhaseChip $status={ phase.status }>
              <ChipIcon $status={ phase.status }>
                {STATUS_ICON[phase.status] || '···'}
              </ChipIcon>
              <ChipText $status={ phase.status }>{phase.phase}</ChipText>
            </PhaseChip>
            <StatusLabel $status={ phase.status }>
              {STATUS_LABEL[phase.status] || 'PENDING'}
            </StatusLabel>
          </PhaseItem>
        </React.Fragment>
      ))}
    </TimelinePhases>
  );
};

const WorkspaceHeader = ({ dashboardType }) => {
  const wsName = useSelector(selectCurrentWorkspace);
  const assetsCount = useSelector(selectAllHostsCountWs);
  const activeWorkspaces = useSelector(selectActiveWorkspaces);
  const pentestStatusData = useSelector(selectPentestStatus);
  const intl = useIntl();
  const translations = useMemo(
    () => ({
      active: intl.formatMessage({ id: 'dashboard.widgets.workspaceHeader.active' }),
      inactive: intl.formatMessage({ id: 'dashboard.widgets.workspaceHeader.inactive' }),
      created: intl.formatMessage({ id: 'dashboard.widgets.workspaceHeader.created' }),
      monitored: intl.formatMessage({ id: 'dashboard.widgets.workspaceHeader.monitored' }),
      assets: intl.formatMessage({ id: 'dashboard.widgets.workspaceHeader.assets' }),
      engagement: intl.formatMessage({ id: 'dashboard.widgets.pentestEngagement.engagement' }),
      pentest: intl.formatMessage({ id: 'dashboard.widgets.pentestEngagement.pentest' }),
      timeline: intl.formatMessage({ id: 'dashboard.widgets.pentestEngagement.timeline' }),
      started: intl.formatMessage({ id: 'dashboard.widgets.pentestEngagement.started' }),
      due: intl.formatMessage({ id: 'dashboard.widgets.pentestEngagement.due' }),
      thisWeek: intl.formatMessage({ id: 'dashboard.widgets.pentestEngagement.thisWeek' }),
      contracted: intl.formatMessage({ id: 'dashboard.widgets.pentestEngagement.contracted' }),
      openRisk: intl.formatMessage({ id: 'dashboard.widgets.pentestEngagement.metrics.openRisk' }),
      daysRemaining: intl.formatMessage({ id: 'dashboard.widgets.pentestEngagement.metrics.daysRemaining' }),
      workspaceRisk: intl.formatMessage({ id: 'dashboard.widgets.pentestEngagement.metrics.workspaceRisk' })
    }),
    [intl]
  );

  const wsListItem = find(activeWorkspaces, { name: wsName }) || {};
  const active = get(wsListItem, 'active', true);
  const createDate = get(wsListItem, 'create_date', null);

  const isPentest = dashboardType === 'pentest';

  const metrics = pentestStatusData?.metrics || null;
  const timeline = pentestStatusData?.timeline || null;
  const startDate = pentestStatusData?.start_date || null;
  const endDate = pentestStatusData?.end_date || null;

  if (!isPentest) {
    return (
      <Wrapper>
        <div>
          <TitleRow>
            <WorkspaceName>{wsName || '-'}</WorkspaceName>
            <ActiveBadge $active={ active }>
              {active ? translations.active : translations.inactive}
            </ActiveBadge>
          </TitleRow>
          <MetaRow>
            <MetaItem>
              <MetaLabel>{translations.created}</MetaLabel>
              <MetaValue>{formatDate(createDate)}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>{translations.monitored}</MetaLabel>
              <MetaValue>
                {assetsCount} {translations.assets}
              </MetaValue>
            </MetaItem>
          </MetaRow>
        </div>
      </Wrapper>
    );
  }

  const openRisk = metrics?.open_risk || 0;
  const weeklyChange = metrics?.weeklyRiskChange || 0;
  const daysRemaining = metrics?.days_remaining || 0;
  const totalDays = metrics?.totalDays || 0;
  const workspaceRisk = metrics?.workspace_risk || 0;
  const riskChangePercent = metrics?.riskChangePercent || 0;

  const formatVariation = (value) => {
    if (value === 0) return '+0';
    if (value > 0) return `+${value}`;
    return `${value}`;
  };

  const engagementPeriod = startDate
    ? new Intl.DateTimeFormat('en-US', { month: '2-digit', year: 'numeric' }).format(new Date(startDate))
    : null;

  return (
    <Wrapper>
      <TopSection>
        <InfoContainer>
          <div style={ { display: 'flex', gap: '3px' } }>
            <Title>{translations.engagement} </Title>
            {engagementPeriod && <TitleRight>| {engagementPeriod}</TitleRight>}
          </div>
          <div style={ { display: 'flex', gap: '3px' } }>
            <Subtitle>{translations.pentest} | </Subtitle>
            <SubtitleRight>{wsName || '-'}</SubtitleRight>
          </div>
          <div style={ { display: 'flex', gap: '30px' } }>
            <div style={ { display: 'flex', gap: '3px' } }>
              <DateLabel>{translations.started}:</DateLabel>
              <Dates>{formatDate(startDate)}</Dates>
            </div>

            <div style={ { display: 'flex', gap: '3px' } }>
              <DateLabel>{translations.due}:</DateLabel>
              <Dates>{formatDate(endDate)}</Dates>
            </div>
          </div>
        </InfoContainer>

        {metrics && (
          <MetricsContainer>
            <MetricCard>
              <MetricLabel>{translations.openRisk}</MetricLabel>
              <MetricValueStyled>{openRisk}</MetricValueStyled>
              <MetricContext>
                {formatVariation(weeklyChange)} {translations.thisWeek}
              </MetricContext>
            </MetricCard>

            <MetricCard>
              <MetricLabel>{translations.daysRemaining}</MetricLabel>
              <MetricValueStyled>{daysRemaining}</MetricValueStyled>
              <MetricContext>OF {totalDays} {translations.contracted}</MetricContext>
            </MetricCard>

            <MetricCard>
              <MetricLabel>{translations.workspaceRisk}</MetricLabel>
              <GaugeComponent value={ workspaceRisk } />
              <MetricValueStyled>{workspaceRisk}</MetricValueStyled>
              <MetricContext>
                {formatVariation(riskChangePercent)}% {translations.thisWeek}
              </MetricContext>
            </MetricCard>
          </MetricsContainer>
        )}
      </TopSection>
      <Divider />
      {timeline && timeline.length > 0 && (
        <TimelineSection>
          <TimelineTitle>{translations.timeline}</TimelineTitle>
          <EngagementTimeline phases={ timeline } />
        </TimelineSection>
      )}
    </Wrapper>
  );
};

export default WorkspaceHeader;
